import PropTypes from 'prop-types'
import useCountUp from './useCountUp'

const FACTORS = [
  { key: 'addiction', label: 'Habits' },
  { key: 'mentalHealth', label: 'Past week' },
  { key: 'lifestyle', label: 'Lifestyle' },
]
const RANGE = 8 // years, half-width of the diverging scale

function AgeSection({ age, hasLifestyle }) {
  const wellness = useCountUp(age.wellnessAge)
  const delta = age.delta
  const tone = delta <= -2 ? 'ok' : delta >= 8 ? 'alert' : delta > 0 ? 'warn' : ''
  const deltaText = delta === 0 ? 'matches your age' : `${delta > 0 ? '+' : '−'}${Math.abs(delta)} ${Math.abs(delta) === 1 ? 'year' : 'years'}`
  const factors = FACTORS.filter((f) => f.key !== 'lifestyle' || hasLifestyle)

  const summary = delta > 0
    ? `Your habits and the past week put your body about ${Math.abs(delta)} ${Math.abs(delta) === 1 ? 'year' : 'years'} ahead of the calendar. The bars show what moves it.`
    : delta < 0
    ? `Your habits and the past week put you about ${Math.abs(delta)} ${Math.abs(delta) === 1 ? 'year' : 'years'} younger than the calendar.`
    : 'Your habits and the past week roughly cancel out against the calendar.'

  return (
    <section className="section" aria-labelledby="age-h">
      <div className="section__head">
        <h2 id="age-h">BetterStep Age</h2>
        <p className="section__desc">Your age adjusted for what you told us. Each factor adds or removes years; the reference point is someone your age with no risk in any area.</p>
      </div>

      <div className="age">
        <div>
          <div className="age__figures">
            <div className="age__fig age__fig--hero">
              <span className="age__fig-num">{wellness}</span>
              <span className="age__fig-label">BetterStep age</span>
            </div>
            <div className="age__fig">
              <span className="age__fig-num">{age.chronologicalAge}</span>
              <span className="age__fig-label">actual age</span>
            </div>
          </div>
          <span className={`age__delta${tone ? ` age__delta--${tone}` : ''}`}>{deltaText}</span>
          <p className="age__summary" style={{ marginTop: 16 }}>{summary}</p>
        </div>

        <div className="factors" aria-label="Contribution by factor">
          <span className="factors__title">years added or removed</span>
          {factors.map((f) => {
            const v = age.deltaBreakdown?.[f.key] ?? 0
            const w = Math.min(50, (Math.abs(v) / RANGE) * 50)
            return (
              <div key={f.key} className="factor">
                <span className="factor__label">{f.label}</span>
                <div className="factor__track">
                  <span className="factor__zero" />
                  <span
                    className={`factor__bar${v < 0 ? ' factor__bar--ok' : ''}`}
                    style={v < 0 ? { right: '50%', width: `${w}%` } : { left: '50%', width: `${w}%` }}
                  />
                </div>
                <span className="factor__val">{v > 0 ? '+' : v < 0 ? '−' : ''}{Math.abs(v).toFixed(1)}</span>
              </div>
            )
          })}
          {!hasLifestyle && (
            <span className="factors__title" style={{ marginTop: 4 }}>lifestyle is added after the full assessment</span>
          )}
        </div>
      </div>
    </section>
  )
}

AgeSection.propTypes = {
  age: PropTypes.object.isRequired,
  hasLifestyle: PropTypes.bool,
}

export default AgeSection
